"use client";
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const divVariants = cva(
  "flex w-auto flex-row rounded-2xl text-white items-center font-bold text-sm gap-2 justify-center items-center",
  {
    variants: {
      variant: {
        default:
          "border bg-slate-900 text-slate-50 hover:bg-slate-900/90 dark:bg-slate-50 dark:text-slate-900 dark:hover:bg-slate-50/90",
        gradient: "border bg-gradient hover:bg-gradient-secondary",
        primary: "border border-primary hover:bg-primary/10 text-primary ",
        secondary: "border border-secondary bg-secondary",
        accent: "border border-accent bg-accent",
        "primary-dark": "border bg-primary hover:bg-primary/90 text-white ",
        white: "border bg-white text-assistant hover:bg-slate-100",
        assistant:
          "border bg-white border border-assistant text-assistant hover:bg-slate-100 disabled:!p-current disabled:!scale-100 active:scale-[1.015] py-3 px-4 m-0 !h-6 flex gap-2 !p-2 text-sm disabled:opacity-30 disabled:cursor-not-allowed",
        black: "border bg-white text-black hover:bg-gray-secondary",
        social:
          "border border-primary bg-white rounded-full hover:bg-white text-primary",
        account: "bg-white",
        nav: "text-assistant",
      },
      size: {
        default:
          "!h-[42px] py-3 px-4 m-0",
        secondary:
          "px-4 py-3",
        sm: "px-2 py-1",
        md: "h-6 px-3 py-1.5",
        modal:
          "px-3 py-2",
        nav: "py-2 px-4",
        sidebar:
          "px-2 py-3",
      },
      disabled: {
        true: "disabled:cursor-not-allowed disabled:opacity-50 !transition-none", // Disable transitions and remove active states
        false: "transition-all duration-400 ease-in-out active:scale-[1.015] active:py-4 active:my-0.5 active:px-3 active:mx-1", // Keep active states only when not disabled
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      disabled: false,
    },
  },
);

export interface DivProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof divVariants> {
  asChild?: boolean;
}

const Container = React.forwardRef<HTMLDivElement, DivProps>(
  ({ className, variant, size, disabled = false, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "div";
    return (
      <Comp
        className={cn(divVariants({ variant, size, disabled, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Container.displayName = "div";

export { Container, divVariants };